import { useCallback, useEffect, useMemo, useState } from 'react';
import type { KbScopeMode, Project } from './api';
import { useProjectTree } from './project-tree';

/**
 * Which projects an org-level page is looking at.
 *
 * The three cross-project pages — Ask KB, KB Review, Feed — used to take their project from the URL,
 * which meant the page could only ever be about one project and "everything" was unaskable. They now
 * share this: a base project plus how far down the tree to read.
 *
 * `projectId: null` means the organization at the root, so the default is genuinely everything. That
 * is the right default now that content arrives without a stated destination and the router spreads
 * one meeting across several projects — the interesting view is all of it, newest first, narrowed
 * afterwards if you want.
 */
export interface ScopeSelection {
  /** Null = the organization at the root of the tree. */
  projectId: string | null;
  /** `cone` includes everything beneath the base project; `self` is that project alone. */
  mode: KbScopeMode;
}

const STORAGE_KEY = 'cdr.scope';

/**
 * One selection shared by every page rather than one each.
 *
 * Someone narrowing the feed to a project and then opening the review queue means the same thing both
 * times; making them re-pick would read as the app forgetting. Persisted for the same reason — a
 * reload is not a change of subject.
 */
function readStored(): ScopeSelection {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { projectId: null, mode: 'cone' };
    const parsed = JSON.parse(raw) as Partial<ScopeSelection>;
    return {
      projectId: typeof parsed.projectId === 'string' ? parsed.projectId : null,
      mode: parsed.mode === 'self' ? 'self' : 'cone',
    };
  } catch {
    return { projectId: null, mode: 'cone' };
  }
}

export interface ScopeState extends ScopeSelection {
  /** The base project's id, resolved to the organization when nothing is chosen. Null while loading. */
  resolvedProjectId: string | null;
  /** The base project itself, for labelling. Null while loading. */
  project: Project | null;
  /** True when reading the whole organization — the default, and what "All projects" means. */
  isEverything: boolean;
  setProjectId: (projectId: string | null) => void;
  setMode: (mode: KbScopeMode) => void;
  /** Maps a project id from a result row back to a project, for per-row labels and links. */
  projectFor: (projectId: string | null | undefined) => Project | null;
}

export function useScope(): ScopeState {
  const { allProjects, organization, loading } = useProjectTree();
  const [selection, setSelection] = useState<ScopeSelection>(readStored);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
    } catch {
      // A private-mode browser refusing storage is not a reason to break the page.
    }
  }, [selection]);

  // A stored id can outlive the project it names — deleted, or belonging to another environment the
  // same browser was pointed at. Falling back to the organization keeps the page working instead of
  // querying a project that no longer exists.
  const known = selection.projectId
    ? (allProjects.find((p) => p.id === selection.projectId) as Project | undefined)
    : undefined;
  const base = known ?? ((organization ?? null) as Project | null);

  useEffect(() => {
    if (!loading && selection.projectId && !known) {
      setSelection((prev) => ({ ...prev, projectId: null }));
    }
  }, [loading, selection.projectId, known]);

  const byId = useMemo(
    () => new Map((allProjects as Project[]).map((p) => [p.id, p])),
    [allProjects],
  );

  const setProjectId = useCallback((projectId: string | null) => {
    // Picking "All projects" also resets the depth: "the organization alone" is a coherent but
    // almost never intended reading, and it looks like an empty app.
    setSelection((prev) => ({ projectId, mode: projectId === null ? 'cone' : prev.mode }));
  }, []);

  const setMode = useCallback((mode: KbScopeMode) => {
    setSelection((prev) => ({ ...prev, mode }));
  }, []);

  const projectFor = useCallback(
    (projectId: string | null | undefined) => (projectId ? byId.get(projectId) ?? null : null),
    [byId],
  );

  return {
    projectId: selection.projectId,
    mode: selection.mode,
    resolvedProjectId: base?.id ?? null,
    project: base,
    isEverything: selection.projectId === null && selection.mode === 'cone',
    setProjectId,
    setMode,
    projectFor,
  };
}
